import { useMemo, useRef, useEffect, useState } from 'react';
import './ProjectGanttChart.css';
import subtaskExpandIcon from '../../assets/subtask-expand.svg';
import subtaskCollapseIcon from '../../assets/subtask-collapse.svg';

const ProjectGanttChart = ({ tasks, onTaskClick }) => {
    const sidebarRef = useRef(null);
    const timelineRef = useRef(null);
    const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());

    const handleToggle = (e, taskId) => {
        e.stopPropagation();
        setExpandedTaskIds(prev => {
            const next = new Set(prev);
            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            return next;
        });
    };

    // Sync vertical scrolling
    useEffect(() => {
        const sidebar = sidebarRef.current;
        const timeline = timelineRef.current;

        if (!sidebar || !timeline) return;

        const handleSidebarScroll = () => {
            if (timeline.scrollTop !== sidebar.scrollTop) {
                timeline.scrollTop = sidebar.scrollTop;
            }
        };

        const handleTimelineScroll = () => {
            if (sidebar.scrollTop !== timeline.scrollTop) {
                sidebar.scrollTop = timeline.scrollTop;
            }
        };

        sidebar.addEventListener('scroll', handleSidebarScroll);
        timeline.addEventListener('scroll', handleTimelineScroll);

        return () => {
            sidebar.removeEventListener('scroll', handleSidebarScroll);
            timeline.removeEventListener('scroll', handleTimelineScroll);
        };
    }, []);

    // Build flat rows list: parent tasks + subtasks for expanded ones
    const rows = useMemo(() => {
        const result = [];
        (tasks || []).forEach(task => {
            result.push({ ...task, _isSubtask: false });
            if (expandedTaskIds.has(task.id) && task.subtasks?.length > 0) {
                task.subtasks.forEach(sub => {
                    result.push({ ...sub, _isSubtask: true });
                });
            }
        });
        return result;
    }, [tasks, expandedTaskIds]);

    // 1. Calculate the date range for the timeline (include subtask dates)
    const { days, weeks, rangeStart } = useMemo(() => {
        if (!tasks || tasks.length === 0) {
            return { days: [], weeks: [], rangeStart: new Date() };
        }

        let minDate = null;
        let maxDate = null;

        const processDate = (task) => {
            const start = task.start_date ? new Date(task.start_date) : null;
            const end = (task.end_date || task.due_date) ? new Date(task.end_date || task.due_date) : null;

            if (start && (!minDate || start < minDate)) minDate = start;
            if (end && (!maxDate || end > maxDate)) maxDate = end;
            if (start && (!maxDate || start > maxDate)) maxDate = start;
        };

        tasks.forEach(task => {
            processDate(task);
            task.subtasks?.forEach(sub => processDate(sub));
        });

        // Default range if no dates found
        if (!minDate) minDate = new Date();
        if (!maxDate) maxDate = new Date(minDate.getTime() + 7 * 24 * 60 * 60 * 1000);

        const rangeStart = new Date(minDate);
        rangeStart.setDate(rangeStart.getDate() - 7);
        rangeStart.setHours(0, 0, 0, 0);

        const rangeEnd = new Date(maxDate);
        rangeEnd.setDate(rangeEnd.getDate() + 14);
        rangeEnd.setHours(23, 59, 59, 999);

        const days = [];
        const weeks = [];
        let current = new Date(rangeStart);

        while (current <= rangeEnd) {
            const day = new Date(current);
            days.push(day);

            const weekLabel = `${day.toLocaleDateString('en-US', { month: 'long' })}`;
            const yearLabel = day.getFullYear();
            const weekNum = Math.ceil(day.getDate() / 7);
            const fullWeekLabel = `Week ${weekNum} (${weekLabel} ${yearLabel})`;

            if (weeks.length === 0 || weeks[weeks.length - 1].label !== fullWeekLabel) {
                weeks.push({ label: fullWeekLabel, daysCount: 1 });
            } else {
                weeks[weeks.length - 1].daysCount++;
            }

            current.setDate(current.getDate() + 1);
        }

        return { days, weeks, rangeStart };
    }, [tasks]);

    const dayWidth = 40;

    const getBarStyles = (task) => {
        const startStr = task.start_date;
        const endStr = task.end_date || task.due_date;

        if (!startStr) return { display: 'none' };

        const start = new Date(startStr);
        const end = endStr ? new Date(endStr) : new Date(start);

        const actualStart = start < end ? start : end;
        const actualEnd = start < end ? end : start;

        const leftDays = Math.floor((actualStart - rangeStart) / (1000 * 60 * 60 * 24));
        const durationDays = Math.ceil((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;

        return {
            left: `${leftDays * dayWidth}px`,
            width: `${durationDays * dayWidth - 4}px`,
        };
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const isWeekend = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const getAssigneeName = (task) => {
        if (task.assignee) return task.assignee.name || task.assignee.full_name || 'Unassigned';
        if (task.assignee_name) return task.assignee_name;
        return 'Unassigned';
    };

    if (!tasks || tasks.length === 0) {
        return (
            <div className="gantt-chart-container">
                <div className="gantt-empty">
                    <h3>No tasks found for the selected range</h3>
                    <p>Try adjusting your filters or adding new tasks with dates.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="gantt-chart-container">
            <div className="gantt-dual-pane">
                {/* Sidebar Pane */}
                <div className="gantt-pane-sidebar" ref={sidebarRef}>
                    <div className="gantt-sidebar-header">
                        <div className="gantt-header-cell gantt-col-task-header">Task</div>
                        <div className="gantt-header-cell gantt-col-resources-header">Resources</div>
                    </div>
                    <div className="gantt-sidebar-body">
                        {rows.map((row, idx) => (
                            <div
                                key={`${row._isSubtask ? 'sub' : 'task'}-${row.id}-${idx}`}
                                className={`gantt-sidebar-row${row._isSubtask ? ' gantt-subtask-sidebar-row' : ''}`}
                            >
                                <div className="gantt-row-cell gantt-col-task" title={row.title}>
                                    {!row._isSubtask && row.subtasks_count > 0 && (
                                        <img
                                            src={expandedTaskIds.has(row.id) ? subtaskCollapseIcon : subtaskExpandIcon}
                                            alt="toggle"
                                            className="gantt-subtask-toggle"
                                            onClick={(e) => handleToggle(e, row.id)}
                                        />
                                    )}
                                    {row._isSubtask && (
                                        <span className="gantt-subtask-indent">└─</span>
                                    )}
                                    <span className="gantt-task-title-text">
                                        {row.title?.length > 20 ? `${row.title.substring(0, 20)}...` : row.title}
                                    </span>
                                </div>
                                <div className="gantt-row-cell gantt-col-resources" title={getAssigneeName(row)}>
                                    {getAssigneeName(row)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Timeline Pane */}
                <div className="gantt-pane-timeline" ref={timelineRef}>
                    <div className="gantt-timeline-header">
                        <div className="gantt-weeks-row">
                            {weeks.map((week, i) => (
                                <div
                                    key={i}
                                    className="gantt-week-cell"
                                    style={{ width: `${week.daysCount * dayWidth}px` }}
                                >
                                    {week.label}
                                </div>
                            ))}
                        </div>
                        <div className="gantt-days-row">
                            {days.map((day, i) => (
                                <div
                                    key={i}
                                    className={`gantt-day-cell ${isToday(day) ? 'today' : ''} ${isWeekend(day) ? 'weekend' : ''}`}
                                >
                                    <span className="gantt-day-name">
                                        {day.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}
                                    </span>
                                    <span className="gantt-day-num">{day.getDate()}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="gantt-timeline-body">
                        <div className="gantt-grid-overlay">
                            {days.map((day, i) => (
                                <div key={i} className={`gantt-grid-line ${isWeekend(day) ? 'weekend' : ''}`} />
                            ))}
                        </div>

                        {rows.map((row, idx) => (
                            <div
                                key={`bar-${row._isSubtask ? 'sub' : 'task'}-${row.id}-${idx}`}
                                className={`gantt-task-row${row._isSubtask ? ' gantt-subtask-task-row' : ''}`}
                            >
                                <div
                                    className={`gantt-bar${row._isSubtask ? ' gantt-subtask-bar' : ''}`}
                                    style={getBarStyles(row)}
                                    onClick={() => !row._isSubtask && onTaskClick && onTaskClick(row)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectGanttChart;
